/**
 * Controller bindings — the record, its defaults, and the key the router finds it by.
 *
 * A binding is `{ source, targets[] }`. The source is backend-specific (`{ backend: 'midi', … }`, later `osc`); everything downstream of `sourceKey` is not. Targets are plural from the start, so macros are a UI feature rather than a migration (ui-migration M-open-1).
 *
 * Target strings:
 *   `param:<live path>`        an effect or source param — `filter.blur`, `source_settings.<source>.<key>`
 *   `param:@selected.<prop>`   the same prop on whatever object is selected. Object ids (`path_1`) are reused across presets, so a fixed id silently retargets.
 *   `param:@source.<key>`      the key on whatever source the fill target shows, so one control drives Speed through a shader switch
 *   `param:<any of these>#hue`, `#brightness`  one HSV channel of a colour param, the others kept
 *   `action:<registry name>`   fired through `triggerAction`
 *   `transport:crossfader`     the preset fade
 *   `transport:presets`        every preset in the list, as a range: the entry's `offset` turns a source number into a preset index
 *   `tempo:bpm`, `tempo:offset` the project clock's BPM and beat offset
 *   `playback:<key>`          a playback setting — `hold_duration`, `fade_duration`, `loop`, `instant` (loads cut)
 *
 * See context/input.md.
 */
import { v4 as uuid } from 'uuid'
import { CHANNELS } from './colorChannels'
import { midiKey } from './midi/devices'

export const MODES = ['absolute', 'relative', 'toggle', 'momentary', 'trigger', 'velocity']
export const TAKEOVERS = ['pickup', 'jump', 'scale']
export const CURVES = ['linear', 'exp', 'log']

// Modes that read a control as a button: a CC crossing the midpoint is a press or a release.
export const BUTTON_MODES = new Set(['toggle', 'momentary', 'trigger'])

const SELECTED = '@selected.'
export const SOURCE_RELATIVE = '@source.'
const PLAYBACK_KEYS = new Set(['hold_duration', 'fade_duration', 'loop', 'instant'])

/** `'param:filter.blur'` → `{ kind: 'param', path }`; null when malformed. `selected` is the prop of a `@selected` target, `sourceKey` the key of a `@source` one, `channel` a colour's `#hue` or `#brightness`. */
export const parseTarget = target => {
	if (typeof target !== 'string') return null
	const i = target.indexOf(':')
	if (i <= 0 || i === target.length - 1) return null
	const kind = target.slice(0, i)
	const rest = target.slice(i + 1)
	if (kind === 'param') {
		const hash = rest.indexOf('#')
		const path = hash < 0 ? rest : rest.slice(0, hash)
		const channel = hash < 0 ? undefined : rest.slice(hash + 1)
		if (channel !== undefined && !CHANNELS[channel]) return null
		const spec = { kind, path, ...(channel && { channel }) }
		const field = path.startsWith(SELECTED) ? 'selected' : path.startsWith(SOURCE_RELATIVE) ? 'sourceKey' : null
		if (!field) return spec
		const prop = path.slice(path.indexOf('.') + 1)
		return prop && !prop.includes('.') ? { ...spec, [field]: prop } : null
	}
	if (kind === 'action') return { kind, name: rest }
	if (kind === 'transport' && rest === 'crossfader') return { kind: 'crossfader' }
	if (kind === 'transport' && rest === 'presets') return { kind: 'presets' }
	if (kind === 'tempo' && (rest === 'bpm' || rest === 'offset')) return { kind, key: rest }
	if (kind === 'playback' && PLAYBACK_KEYS.has(rest)) return { kind, key: rest }
	return null
}

/** A target entry: `min`/`max` are 0…1 of the target's own domain (Resolume's Range), so a binding survives a schema range change. */
export const makeTarget = (target, patch) => ({ min: 0, max: 1, invert: false, curve: 'linear', ...patch, target })

export const makeBinding = ({ id, source, targets = [], ...rest }) => ({
	mode: 'absolute',
	// Relative only; picked at learn time from what the encoder sends.
	encoding: 'twos',
	takeover: 'pickup',
	// ms, 0 = off. Recorded, not applied yet.
	smoothing: 0,
	enabled: true,
	...rest,
	id: id ?? uuid(),
	source,
	targets: targets.map(t => (typeof t === 'string' ? makeTarget(t) : makeTarget(t.target, t))),
})

/**
 * The string the router indexes a binding's source by. A backend's decoded event carries the same key.
 *
 * A MIDI key names no device: the same message from any controller is the same control.
 */
export const sourceKey = source => {
	if (source?.backend === 'midi') {
		const { channel, type, number } = source
		return midiKey(channel, type, number)
	}
	if (source?.backend === 'osc') return `osc|${source.address}`
	return null
}

export const PRESETS_TARGET = 'transport:presets'

// MIDI numbers run 0…127, so a range reaches at most 128 presets.
export const RANGE_SIZE = 128

/** The range entry of a binding, or undefined: a binding on `transport:presets` answers every number of its message type. */
export const rangeEntry = binding => binding?.targets?.find(t => t.target === PRESETS_TARGET)

/** Do two bindings answer the same physical control? Learn's "one mapping per message" default moves rather than adds. */
export const sameSource = (a, b) => {
	const key = sourceKey(a?.source)
	return key !== null && key === sourceKey(b?.source)
}
